import {
    convertToBytes,
    getBackgroundColorForFile,
    PDFTRON_VIEWABLE_FILE_TYPES,
    useFileTypeImage,
} from '@components/file/fileUtilities';
import { useRouter } from 'next/router';
import {
    ActivityStatusOverride,
    CommentableObject,
    Course,
    FileEntry,
    Role,
    SingleSubmission,
    StudentSubmission,
    Submission,
    SubmissionStatus,
    useBatchActivityOverridesMutation,
    useCreateFileMutation,
    useOverrideActivityStatusMutation,
    useUpdateAssignmentSubmissionMutation,
} from '@graphql/generated-graphql';
import { getClient } from '@graphql/graphql-client/graphClient';
import {
    ChevronDownIcon,
    ChevronUpIcon,
    DownloadIcon,
    MinusIcon,
    PencilIcon,
    TrashIcon,
    UploadIcon,
} from '@heroicons/react/outline';
import { CheckIcon } from '@heroicons/react/solid';
import { useDispatchError } from '@hooks/error/useDispatchError';
import { useUploaderModalSubmission } from '@hooks/submissions/useAssignmentStartAndSubmit';
import { useInvalidateQueries } from '@hooks/useInvalidateQueries';
import { Button, Input, SkipperAvatar, Text } from '@skipper/ui';
import dayjs from 'dayjs';
import NextImage from 'next/image';
import React, { HTMLProps, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { AssignmentItem } from './AssignmentActionButtons';
import { FileListItem } from '@components/modals/imageSelectModal/ImageSelectModal';
import { useQueryParams } from '@hooks/useQueryParams';
import { SimpleSpinner } from '@components/common/loading/SimpleSpinner';
import { useImageInput } from '@hooks/files/useImageInput';
import { useUserSession } from '@hooks/user/useDefaultSession';
import { useGradeSubmissions } from '@hooks/grading/useGradeSubmissions';
import { StatusItem } from './sharedItems';
import { ActivityStatusOverrideSelector } from '@components/activities/components/ActivityStatusOverrideSelector';
import { ActivityDoneSVG } from '@components/common/svgs/SubmissionStatusSVGs';
import { OpenSVG } from '@components/common/svgs/OpenSVG';
import {
    GradebookAnnotationPostedSVG,
    GradebookAnnotationsSVG,
    GradebookAttachmentSVG,
} from '@components/common/svgs/GradebookAttachmentSVG';

import {
    ColumnDef,
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { ActivityArtistSVG } from '@components/common/svgs/ActivityArtistSVG';
import { tryMakeDownloadUrl } from '@utils/urlPaths';
import useIsMobile from '@hooks/useIsMobile';
import { TurnitinEulaModal } from '@components/modals/TurnItInEulaModal';
import { useUniversalModalDispatch } from '@components/context/UniversalModalContext';
import { SkipperPaperEditor } from '@components/editor/paper/SkipperPaperEditor';
import { SimpleModal } from '@components/base/overlays/Modal/SimpleModal';
import { useCoursePermissions } from '@components/context/CourseContext';

export function AssignmentsSubmissionsView({ activity }: { activity: AssignmentItem }) {
    return (
        <div className="px-[24px]">
            <NoSubmissions activity={activity} />
        </div>
    );
}

function NoSubmissions({ activity }: { activity: AssignmentItem }) {
    const { handleSubmissionModal } = useUploaderModalSubmission({ activity });
    const studentSubmission = activity?.studentSubmissions?.[0];
    const activityStatus = activity?.studentSubmissions[0]?.status;
    const hasSubmissions = !!studentSubmission?.submission?.attachments?.length;
    const showMissedEmptySubmissions =
        !studentSubmission?.submission &&
        activity?.lockedAfterDueDate &&
        activityStatus !== SubmissionStatus.Reassigned &&
        activityStatus !== SubmissionStatus.MarkedDone &&
        activityStatus !== SubmissionStatus.Completed;
    const showUpload =
        (!activity?.lockedAfterDueDate && !activity?.lockedBeforeAssignDate) ||
        activityStatus === SubmissionStatus.Reassigned;
    const activityIsCompleted =
        activityStatus === SubmissionStatus.Completed ||
        activityStatus === SubmissionStatus.MarkedDone;

    const activityIsCompletedOrHasSubmissions = !!hasSubmissions || activityIsCompleted;
    const showNoLongerAcceptingFiles =
        activity?.lockedAfterDueDate && activityStatus !== SubmissionStatus.Reassigned;
    const [files, setFiles] = useState<any[]>([]);
    const [user] = useUserSession();
    const { toggle } = useUniversalModalDispatch();
    const fileCreate = useCreateFileMutation(getClient('files'), {
        onSettled: (data) => {
            if (data?.createFile?.success) {
                setFiles((f) => [
                    ...f,
                    {
                        ...data?.createFile?.fileEntry,
                        file: {
                            ...data?.createFile?.fileEntry,
                        },
                    },
                ]);
            }
        },
    });
    const { handlers } = useImageInput({
        immediateUpdate: true,
        multiple: true,
        updateNow: (file) => {
            fileCreate.mutate({
                title: file.name,
                file: file,
                authorizerItem: {
                    id: user?.id,
                    type: 'User',
                },
            });
        },
    });

    useEffect(() => {
        if (files.length === 0) return;

        let timeout = setTimeout(() => {
            const submitFiles = [...files];
            handleSubmissionModal(null, submitFiles);
            setFiles([]);
        }, 3000);
        return () => clearTimeout(timeout);
    }, [files, handleSubmissionModal]);

    // activity?.studentSubmissions?.filter(s => !!s?.submission).length > 0 &&
    // activity?.submissions?.[activity?.submissions?.length - 1]?.attachments?.length > 0;
    return (
        <>
            <div className="relative flex flex-col w-full items-center justify-center py-[55px]">
                {!hasSubmissions && showUpload && (
                    <div className="flex flex-col gap-y-2 mb-[35px]">
                        <input
                            type="file"
                            className="absolute inset-0 block z-25 w-full opacity-0"
                            onDrop={handlers.fileDropEventHandler}
                            onChange={handlers.fileInputEventHandler}
                            multiple
                        />
                        <ActivityArtistSVG />
                        <p className="text-lg font-[500] text-gray-600 font-inter">
                            No file uploads yet!
                        </p>
                    </div>
                )}
                {hasSubmissions && <SubmissionWithFiles activity={activity} />}
                {showMissedEmptySubmissions && !activityIsCompletedOrHasSubmissions && (
                    <NoSubmissionsAndClosed />
                )}
                {showUpload && (
                    <Button
                        size="large"
                        variant="light"
                        className="w-full max-w-[555px] flex gap-x-2 z-[30]"
                        onClick={() => {
                            if (
                                activity.similarityReportsEnabled &&
                                user.turnitinEulaAcceptedVersion != user.turnitinEulaLatestVersion
                            ) {
                                toggle(
                                    true,
                                    <TurnitinEulaModal
                                        version={user.turnitinEulaLatestVersion}
                                        onAccept={handleSubmissionModal}
                                        variant={'upload'}
                                    />,
                                );
                            } else handleSubmissionModal();
                        }}>
                        <UploadIcon className="w-6 h-6 stroke-0" />
                        Upload files
                    </Button>
                )}
                {showNoLongerAcceptingFiles && !showMissedEmptySubmissions && (
                    <p className="text-m font-[500] text-gray-600 font-inter">
                        This assignment is closed and doesn&apos;t accept file uploads
                    </p>
                )}
            </div>
        </>
    );
}

function NoSubmissionsAndClosed() {
    return (
        <div className="flex flex-col w-full items-center justify-center">
            <div className="flex flex-col gap-y-2 mb-[35px] justify-center items-center">
                <NextImage
                    src="/svgs/activity/no-submissions-locked.svg"
                    layout="fixed"
                    width={153}
                    height={153}
                />
                <p className="text-lg font-[500] text-gray-600 font-inter text-center">
                    Oh no! You missed the due date and your assignment is closed.
                </p>
            </div>
        </div>
    );
}

function SubmissionWithFiles({ activity }: { activity: AssignmentItem }) {
    const submission = activity?.studentSubmissions?.[0]?.submission;
    const { annotationStatus } = submission;
    const { id } = useQueryParams(['id']);
    const invalidateQueries = useInvalidateQueries();
    const sendMessage = useDispatchError();
    const updateSubmission = useUpdateAssignmentSubmissionMutation(getClient('submissions'), {
        onSettled: (data) => {
            if (data?.updateAssignmentSubmission?.success) {
                invalidateQueries('CoursesActivitySubmissionsByEnrollments');
            } else {
                sendMessage(
                    data?.updateAssignmentSubmission?.errors?.[0] || 'Something went wrong',
                );
            }
        },
    });

    const formattedAttachments = useMemo(() => {
        const map = new Map<string, any[]>();
        const attachments = activity?.studentSubmissions?.[0]?.submission?.attachments ?? [];

        attachments.forEach((attachment) => {
            const { createdAt } = attachment;
            const date = dayjs(createdAt).format('MMM DD');
            if (!map.has(date)) {
                map.set(date, []);
            }
            map.get(date).push(attachment);
        });
        const finalResults: { date: string; attachments: FileEntry[] }[] = [];
        for (const [date, attachments] of map) {
            finalResults.push({ date, attachments });
        }

        return finalResults;
    }, [activity]);

    const firstAnnotatable = submission?.attachments?.find((el) => el?.annotatable);
    const annotatedFileName = activity?.item?.title + '_Annotated';

    return (
        <div className="flex flex-col items-center justify-center w-full mb-9 gap-y-3">
            {annotationStatus === 'posted' && (
                <>
                    <div className="flex w-full max-w-[555px] text-sm">
                        <GradebookAnnotationsSVG
                            stroke="#637488"
                            strokeWidth={1.2}
                            width={22}
                            height={22}
                            viewBox="0 0 20 20"
                            className="mr-2"
                        />
                        Annotated
                    </div>
                    <a
                        className="flex justify-between items-center p-2 rounded-md z-[3] bg-lime-50 w-full max-w-[555px]"
                        href={`/courses/${id}/file-viewer?fileId=${submission?.annotatedPdf?.id}`}
                        target="_blank"
                        rel="noreferrer">
                        <div className="flex items-center mr-3 h-full gap-x-2 truncate">
                            <div className="min-w-[39px] flex items-center justify-center">
                                <GradebookAnnotationPostedSVG
                                    width={35}
                                    height={35}
                                    strokeWidth={1}
                                    variant="green"
                                    viewBox="0 0 20 20"
                                />
                            </div>
                            <div className="truncate">
                                <p className="truncate text-sm font-[500] text-passport">
                                    {annotatedFileName}
                                </p>
                                <p className="text-xs text-gray-500 font-[400]">
                                    {`${convertToBytes(firstAnnotatable?.originalFileSize)}\u200b`}
                                </p>
                            </div>
                        </div>
                        <Button
                            as="a"
                            size="small"
                            shape="fixedSquare"
                            variant="white"
                            href={`/courses/${id}/file-viewer?fileId=${submission?.annotatedPdf?.id}`}
                            target="_blank"
                            rel="noreferrer">
                            <OpenSVG className="w-5 h-5 stroke-gray-500" />
                        </Button>
                    </a>
                </>
            )}
            <div className="flex w-full max-w-[555px] mt-3 text-sm">
                <GradebookAttachmentSVG
                    stroke="#637488"
                    strokeWidth={1.2}
                    width={22}
                    height={22}
                    viewBox="0 0 20 20"
                    className="mr-2"
                />
                Files
            </div>
            {formattedAttachments.map(({ date, attachments }) => {
                return (
                    <div key={date} className="flex flex-col gap-y-2 w-full  max-w-[555px]">
                        <DateLine date={date} />
                        {attachments.map((file, index) => {
                            const fileUrl = file?.url;
                            return (
                                <FileListAttachment
                                    key={fileUrl}
                                    item={{ file } as FileListItem}
                                    onDelete={() => {
                                        updateSubmission.mutate({
                                            submissionId: submission?.id,
                                            removeAttachmentIds: [file.id],
                                        });
                                    }}
                                    showDelete={
                                        !!submission.deletableAttachments.find(
                                            (a) => a.id == file.id,
                                        )
                                    }
                                    showDownload={true}
                                />
                            );
                        })}
                    </div>
                );
            })}
        </div>
    );
}

function DateLine({ date }: { date: string }) {
    return (
        <div className="w-full relative">
            <div className="flex w-full items-center justify-center relative">
                <p className="uppercase h-[24px] w-[65px] border-gray-300 text-xs font-inter font-[600] border rounded-xl flex items-center justify-center bg-white z-[1] text-gray-600">
                    {date}
                </p>
                <div className="absolute left-0 right-0 h-[1px] bg-gray-300 translate-y-1/2 z-0" />
            </div>
        </div>
    );
}

const GradeCell: Partial<ColumnDef<TableRow, TableRow['grade']>>['cell'] = (info) => {
    const { grade, submission, enrollment, activity, status, course, dueStatus, numberOfRows } =
        info.getValue();
    const [open, setOpen] = useState(false);
    // ts-ignore
    const [handleGrading, isLoading, overrideActivityStatus] = useGradeSubmissions({});

    const formRow = useForm<TFormValues>({
        defaultValues: {
            grade: grade,
        },
    });

    const isActivityGraded = activity.graded;

    const showLateStatus = !!dueStatus && dueStatus === 'late';

    const activityStatus = showLateStatus ? 'late' : status;

    const isNotGraded = !info.table.options?.meta?.isGraded && status === null;
    const showStatusSelectorDropdown =
        isActivityGraded || showLateStatus
            ? true
            : status !== SubmissionStatus.Completed &&
              status !== SubmissionStatus.MarkedDone &&
              !!status;
    const canMarkDone =
        status !== SubmissionStatus.Completed &&
        status !== SubmissionStatus.MarkedDone &&
        status !== SubmissionStatus.NeedsGrading;

    const [inputId, setInputId] = useState(null);

    return (
        <div className="col-span-5 flex items-center gap-x-1">
            {isActivityGraded && (
                <>
                    <GradeInput
                        {...formRow.register('grade', {
                            onBlur: () => {
                                formRow.handleSubmit((form, context) => {
                                    handleGrading({
                                        submission,
                                        enrollment,
                                        grade: form?.grade,
                                        oldGrade: grade,
                                        activity,
                                        status,
                                    });
                                })();
                            },
                        })}
                        id={'gradeInput_' + info.row.index}
                        height="small"
                        onFocus={(e) => setInputId(info.row.index)}
                        onKeyDown={(e) => {
                            if (e.key === 'Tab' || e.key === 'ArrowDown' || e.key === 'Enter') {
                                e.preventDefault();
                                const nextInputId = inputId + 1 < numberOfRows ? inputId + 1 : 0;
                                const element = document.getElementById(
                                    `gradeInput_${nextInputId}`,
                                );
                                element?.focus();
                            } else if (e.key === 'ArrowUp') {
                                e.preventDefault();
                                const nextInputId =
                                    inputId - 1 >= 0 ? inputId - 1 : numberOfRows - 1;
                                const element = document.getElementById(
                                    `gradeInput_${nextInputId}`,
                                );
                                element?.focus();
                            }
                        }}
                        css={{
                            maxWidth: '48px',
                            px: 0,
                            textAlign: 'center',
                            display: 'inline-block',
                            py: 0,
                            maxHeight: '32px',
                            ...(status === SubmissionStatus.NeedsGrading ||
                            (status === SubmissionStatus.MarkedDone && !grade)
                                ? { border: '1px solid $magenta600' }
                                : {}),
                        }}
                    />
                    <Text
                        as="span"
                        size="small"
                        css={{
                            color: '$gray700',
                            fontWeight: 500,
                            marginRight: '10px',
                        }}>{` /${activity?.item?.maxPoints}`}</Text>
                </>
            )}
            <StatusItem
                status={activityStatus}
                grade={grade}
                maxPoints={activity?.item?.maxPoints}
                gradingScheme={course?.gradingScheme}
                isGraded={!!activity?.graded}
                // gradePercentage={grade}
                isAssignmentGraded={isActivityGraded}
            />
            {showStatusSelectorDropdown && (
                <ActivityStatusOverrideSelector
                    open={open}
                    setOpen={setOpen}
                    onSelection={(status) => {
                        overrideActivityStatus({
                            activity,
                            enrollment,
                            statusOverride: status,
                        });
                    }}
                    canMarkDone={canMarkDone}>
                    <div className="flex gap-x-1 items-center">
                        <span className="flex flex-col justify-around items-center">
                            <ChevronUpIcon className="w-3 h-3 stroke-gray-400 -mb-1" />
                            <ChevronDownIcon className="w-3 h-3 stroke-gray-400" />
                        </span>
                    </div>
                </ActivityStatusOverrideSelector>
            )}
            {isLoading && <SimpleSpinner isSmall />}
        </div>
    );
};

const MarkDoneAndFiles: Partial<ColumnDef<TableRow, TableRow['grade']>>['cell'] = (info) => {
    const { submission, enrollment, activity, status, grade, course } = info.getValue();
    const dispatch = useDispatchError();
    const invalidateQueries = useInvalidateQueries();
    const { handleSubmissionModal } = useUploaderModalSubmission({
        activity,
        enrollmentId: enrollment?.id,
    });
    const [handleGrading, isLoading, overrideActivityStatus] = useGradeSubmissions({});
    const [user] = useUserSession();
    const { toggle } = useUniversalModalDispatch();

    const attachments = useMemo(() => {
        return (submission as Submission)?.attachments ?? [];
    }, [submission]);

    const additionalFiles = useMemo(() => {
        const files = attachments.filter((f) => !f?.annotatable) ?? [];

        return files;
    }, [attachments]);

    const annotationStatus = useMemo(() => {
        return (submission as Submission)?.annotationStatus;
    }, [submission]);

    const assignmentGradedAt = useMemo(() => {
        return (submission as Submission)?.gradedAt;
    }, [submission]);

    const lastAnnotatableUploadedAt = useMemo(() => {
        return (submission as Submission)?.attachments?.findLast((el) => el?.annotatable)
            ?.createdAt;
    }, [submission]);

    const newAttachmentAfterGrading = dayjs(lastAnnotatableUploadedAt) > dayjs(assignmentGradedAt);

    const exceptionApplied =
        status === SubmissionStatus.Absent ||
        status === SubmissionStatus.Dropped ||
        status === SubmissionStatus.Excused ||
        status === SubmissionStatus.Skipped;
    const exceptionWithoutFiles = exceptionApplied && attachments.length === 0;

    const annotationIconColor =
        (!grade || newAttachmentAfterGrading) &&
        (status === SubmissionStatus.NeedsGrading || annotationStatus === 'new') &&
        !exceptionApplied &&
        status !== SubmissionStatus.Reassigned
            ? '#FF337C'
            : '#3B516A';

    const markDone = useOverrideActivityStatusMutation(getClient('courses'), {
        onSettled: async (data) => {
            if (data?.overrideActivityStatus?.success) {
                await invalidateQueries(
                    'CoursesWithActivitiesAndSubmissions',
                    'CourseUnitActivitiesData',
                    'CoursesActivitySubmissionsByEnrollments',
                );
            } else {
                dispatch(data?.overrideActivityStatus?.errors?.[0] || 'Something went wrong');
            }
        },
    });

    const showActivitySvgIcon =
        status === SubmissionStatus.MarkedDone ||
        status === SubmissionStatus.NeedsGrading ||
        status === SubmissionStatus.Completed ||
        !!grade ||
        exceptionWithoutFiles ||
        attachments.length > 0;

    const isActivityGraded = activity?.graded;

    const hideAnnotationButton =
        attachments.length > 0 &&
        attachments.length - additionalFiles.length === 0 &&
        !exceptionWithoutFiles;

    const hideMarkDoneButton =
        (isActivityGraded &&
            activity?.item?.onlineSubmissionRequired &&
            activity?.item?.submissionKind === 'online') ||
        showActivitySvgIcon ||
        exceptionWithoutFiles;

    const color =
        status === SubmissionStatus.MarkedDone || status === SubmissionStatus.NeedsGrading
            ? '#FF337C'
            : '#637488';

    const router = useRouter();
    const isMobile = useIsMobile();

    return (
        <div className="col-span-2 flex justify-end">
            {showActivitySvgIcon && (
                <>
                    {!hideAnnotationButton && (
                        <Button
                            variant="white"
                            size={isMobile ? 'xsmall' : 'medium'}
                            paddings={isMobile ? 'smallPaddings' : ''}
                            className="flex gap-x-2 md:w-[40px] md:h-[40px] md:!p-2"
                            type="button"
                            onClick={() => {
                                router.push(
                                    `/courses/${course?.id}/file-viewer/${enrollment?.id}/${activity?.id}?url=${submission?.annotatedPdf?.url}&submissionId=${submission?.id}&prevPath=tab`,
                                );
                            }}>
                            {attachments.length === 0 && (
                                <ActivityDoneSVG
                                    fill={isActivityGraded ? color : '#637488'}
                                    stroke={isActivityGraded ? color : '#637488'}
                                    strokeWidth={1.5}
                                    width={25}
                                    height={25}
                                    viewBox="0 0 20 20"
                                />
                            )}
                            {attachments.length > 0 && (
                                <>
                                    {annotationStatus === 'posted' ? (
                                        <GradebookAnnotationPostedSVG strokeWidth={1} />
                                    ) : (
                                        <GradebookAnnotationsSVG
                                            stroke={
                                                annotationStatus === 'in_progress'
                                                    ? '#1F6CD5'
                                                    : annotationIconColor
                                            }
                                            strokeWidth={1.2}
                                            width={25}
                                            height={25}
                                            viewBox="0 0 20 20"
                                        />
                                    )}
                                </>
                            )}
                        </Button>
                    )}
                    {additionalFiles?.length > 0 && (
                        <Button
                            size={isMobile ? 'xsmall' : 'medium'}
                            paddings={isMobile ? 'smallPaddings' : ''}
                            variant="white"
                            className="flex gap-x-2 w-[40px] h-[40px] md:!p-2 ml-1"
                            type="button">
                            <GradebookAttachmentSVG stroke={color} />
                        </Button>
                    )}
                </>
            )}
            {!hideMarkDoneButton && (
                <Button
                    size={isMobile ? 'xsmall' : 'medium'}
                    paddings={isMobile ? 'smallPaddings' : ''}
                    variant="lime"
                    type="button"
                    onClick={() => {
                        markDone.mutate({
                            enrollmentId: enrollment?.id,
                            id: activity.id,
                            status: ActivityStatusOverride.MarkedDone,
                        });
                    }}>
                    <CheckIcon className="h-4 w-4 stroke-white items-center" />
                </Button>
            )}
            <Button
                size={isMobile ? 'xsmall' : 'medium'}
                paddings={isMobile ? 'smallPaddings' : ''}
                variant="white"
                className="ml-1 w-[40px] h-[40px] md:!p-1"
                type="button"
                onClick={() => {
                    const handler = () => {
                        handleSubmissionModal(null, null, () => {
                            overrideActivityStatus({
                                activity,
                                enrollment,
                                statusOverride: null,
                            });
                        });
                    };
                    if (
                        activity.similarityReportsEnabled &&
                        user.turnitinEulaAcceptedVersion != user.turnitinEulaLatestVersion
                    ) {
                        toggle(
                            true,
                            <TurnitinEulaModal
                                version={user.turnitinEulaLatestVersion}
                                onAccept={handler}
                                variant={'upload'}
                            />,
                        );
                    } else handler();
                }}>
                <UploadIcon stroke="#637488" className="w-6 h-6" strokeWidth={1.7} />
            </Button>
        </div>
    );
};

type TableRow = {
    enrollment: any;
    grade: any;
    activity: any;
    empty: any;
};

const helper = createColumnHelper<TableRow>();

export function AssignmentSubmissionsInstructorView({
    activity,
    submissions,
    course,
}: {
    activity: AssignmentItem;
    submissions: (StudentSubmission & SingleSubmission)[];
    course?: Course;
}) {
    const router = useRouter();
    const isActivityGraded = activity?.graded;
    const isCourseGraded = !!course?.graded;
    const isGraded = isCourseGraded && isActivityGraded;
    const [showBulActionsDropdown, setShowBulkActionsDropdown] = useState(false);
    const { isCourseRole } = useCoursePermissions();

    const columns = useMemo(
        () => [
            helper.display({
                id: 'select',
                header: ({ table }) => (
                    <div className="px-1 col-span-1">
                        <IndeterminateCheckbox
                            setShowBulkActionsDropdown={setShowBulkActionsDropdown}
                            {...{
                                checked: table.getIsAllRowsSelected(),
                                indeterminate: table.getIsSomeRowsSelected(),
                                someRowsSelected: table.getIsSomeRowsSelected(),
                                allRowsSelected: table.getIsAllRowsSelected(),
                                onChange: table.getToggleAllRowsSelectedHandler(),
                                selectedRows: table.getSelectedRowModel().flatRows,
                            }}
                        />
                    </div>
                ),
                cell: ({ row }) => (
                    <div className="px-1 col-span-1">
                        <IndeterminateCheckbox
                            setShowBulkActionsDropdown={setShowBulkActionsDropdown}
                            {...{
                                checked: row.getIsSelected(),
                                indeterminate: row.getIsSomeSelected(),
                                someRowsSelected: table.getIsSomeRowsSelected(),
                                allRowsSelected: table.getIsAllRowsSelected(),
                                onChange: row.getToggleSelectedHandler(),
                                selectedRows: table.getSelectedRowModel().flatRows,
                            }}
                        />
                    </div>
                ),
            }),
            helper.accessor('enrollment', {
                id: 'name',
                header: () => <div className="col-span-4">Name</div>,
                cell: (info) => (
                    <div className="col-span-4">
                        <div className="flex flex-1 w-full items-center gap-x-4">
                            <SkipperAvatar
                                {...info.getValue().user}
                                imageUrl={info.getValue()?.user?.imageFileEntry?.url}
                                size="medium"
                            />
                            <h2 className="text-sm text-passport">
                                {isCourseRole(Role.Leader) ? (
                                    <div
                                        className="text-primary hover:text-primary-hover cursor-pointer"
                                        onClick={() =>
                                            router.push(
                                                `/courses/${course.id}/file-viewer/${
                                                    info.getValue().id
                                                }/${activity.id}?prevPath=tab`,
                                            )
                                        }>
                                        {info.getValue()?.user?.fullName}
                                    </div>
                                ) : (
                                    info.getValue()?.user?.fullName
                                )}
                            </h2>
                        </div>
                    </div>
                ),
                footer: null,
            }),
            helper.accessor('grade', {
                header: () => <div className="col-span-5">{isGraded ? 'Grade' : ''}</div>,
                cell: GradeCell,
                footer: null,
            }),
            helper.accessor('empty', {
                header: () => (
                    <div className={isCourseGraded ? 'col-span-2' : 'col-span-4'}>&nbsp;</div>
                ),
                cell: MarkDoneAndFiles,
                footer: null,
            }),
        ],
        [activity?.id, course],
    );

    const sortedSubmissions = submissions.sort((a, b) =>
        a?.enrollment?.user?.fullName.localeCompare(b?.enrollment?.user?.fullName),
    );

    const [data, setData] = useState<TableRow[]>(
        sortedSubmissions?.map(({ enrollment, grade, status, submission }) => {
            return {
                enrollment,
                grade: {
                    status,
                    grade,
                    enrollment,
                    course,
                    activity,
                    isGraded,
                    numberOfRows: sortedSubmissions?.length,
                },
                activity: { status, grade, enrollment, course, activity, isGraded },
                empty: {
                    status,
                    grade,
                    enrollment,
                    course,
                    activity,
                    submission,
                    isGraded,
                },
            };
        }),
    );
    const dispatch = useDispatchError();
    const invalidate = useInvalidateQueries();
    const batchOverride = useBatchActivityOverridesMutation(getClient('courses'), {
        onSettled: async (data) => {
            if (data?.batchActivityOverrides?.success) {
                await invalidate(
                    'CourseUnitActivitiesData',
                    'CourseMetaData',
                    'CourseUnitsData',
                    'CoursesActivitySubmissionsByEnrollments',
                );
            } else {
                dispatch(data?.batchActivityOverrides?.errors?.[0] ?? 'Something went wrong');
            }
        },
    });
    const [rowSelection, setRowSelection] = React.useState({});

    useEffect(() => {
        setData(() =>
            sortedSubmissions?.map(({ enrollment, grade, status, submission, dueStatus }) => {
                return {
                    enrollment,
                    grade: {
                        status,
                        grade,
                        enrollment,
                        course,
                        activity,
                        isGraded,
                        dueStatus,
                        numberOfRows: sortedSubmissions?.length,
                    },
                    activity: { status, grade, enrollment, course, activity, isGraded },
                    empty: { status, grade, enrollment, course, activity, submission, isGraded },
                };
            }),
        );
    }, [sortedSubmissions, activity, course, isGraded]);

    const table = useReactTable({
        columns: columns as typeof columns,
        data,
        getCoreRowModel: getCoreRowModel(),
        state: {
            rowSelection,
        },
        onRowSelectionChange: setRowSelection,
        filterFns: {
            fuzzy: (rows, id, filterValue) => true,
        },
        meta: {
            isGraded,
        },
    });

    const [open, setOpen] = useState(false);

    return (
        <React.Fragment>
            {showBulActionsDropdown && (
                <div className="px-[24px] mt-[50px] flex">
                    <div className="flex items-center border rounded-sm max-w-fit h-[38px] ml-[18px] border-gray-300">
                        <div className="px-[9px] border-r h-full">
                            <IndeterminateCheckbox
                                setShowBulkActionsDropdown={setShowBulkActionsDropdown}
                                {...{
                                    checked: table.getIsAllRowsSelected(),
                                    indeterminate: table.getIsSomeRowsSelected(),
                                    someRowsSelected: table.getIsSomeRowsSelected(),
                                    allRowsSelected: table.getIsAllRowsSelected(),
                                    onChange: table.getToggleAllRowsSelectedHandler(),
                                    selectedRows: table.getSelectedRowModel().flatRows,
                                }}
                            />
                        </div>
                        <ActivityStatusOverrideSelector
                            open={open}
                            setOpen={setOpen}
                            canMarkDone
                            onSelection={(status) => {
                                const selectedRowSelections = table.getSelectedRowModel().flatRows;
                                const batchOverrideInputs = selectedRowSelections.map((row) => {
                                    const id = row?.original?.activity?.activity?.id;
                                    const enrollmentId = row?.original?.enrollment?.id;
                                    return {
                                        status,
                                        id: id,
                                        enrollmentId: enrollmentId,
                                    };
                                });
                                if (batchOverrideInputs.length > 0) {
                                    batchOverride.mutate({
                                        overrides: batchOverrideInputs,
                                    });
                                }
                            }}>
                            <div className="px-3 flex gap-x-2 items-center h-full text-gray-700">
                                Select bulk action
                                <span>
                                    <ChevronDownIcon className="w-4 h-4 stroke-gray-400" />
                                </span>
                            </div>
                        </ActivityStatusOverrideSelector>
                    </div>
                    {batchOverride.isLoading && (
                        <div className="ml-4 inline-block">
                            <SimpleSpinner isSmall />
                        </div>
                    )}
                </div>
            )}
            <div className="pt-[26px] px-[24px] w-full mb-[96px]">
                <div className="w-full grid grid-cols-1 border border-gray-200 rounded-md divide-y divide-gray-200">
                    {table.getHeaderGroups().map((headerGroup) => {
                        return (
                            <div
                                key={headerGroup.id}
                                className="grid grid-cols-12 col-span-1 py-3 px-6 text-gray-500 text-sm">
                                {headerGroup.headers.map((header) => {
                                    if (header.isPlaceholder) return null;
                                    return (
                                        <React.Fragment key={header.id}>
                                            {flexRender(
                                                header.column.columnDef.header,
                                                header.getContext(),
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        );
                    })}
                    {table.getRowModel().rows.map((row) => {
                        return (
                            <div
                                key={row.id}
                                className="grid grid-cols-12 col-span-1 px-1 md:px-6 min-h-[64px] items-center h-fit">
                                {row.getVisibleCells().map((cell) => {
                                    return (
                                        <React.Fragment key={cell.id}>
                                            {flexRender(
                                                cell.column.columnDef.cell,
                                                cell.getContext(),
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            </div>
        </React.Fragment>
    );
}
function IndeterminateCheckbox({
    setShowBulkActionsDropdown,
    indeterminate,
    someRowsSelected,
    allRowsSelected,
    selectedRows,
    className = 'relative appearance-none border border-gray-200 focus:outline-primary  rounded-sm w-5 h-5 indeterminate:bg-teal-600 checked:bg-teal-600 checked:after:text-white',
    ...rest
}: {
    indeterminate?: boolean;
    setShowBulkActionsDropdown?: any;
    someRowsSelected?: boolean;
    allRowsSelected?: boolean;
    selectedRows?: any;
} & HTMLProps<HTMLInputElement>) {
    const ref = React.useRef<HTMLInputElement>(null!);

    React.useEffect(() => {
        if (typeof indeterminate === 'boolean') {
            ref.current.indeterminate = !rest.checked && indeterminate;
        }
    }, [ref, indeterminate]);

    useEffect(() => {
        someRowsSelected || allRowsSelected
            ? setShowBulkActionsDropdown(true)
            : setShowBulkActionsDropdown(false);
    }, [someRowsSelected, allRowsSelected, setShowBulkActionsDropdown]);

    return (
        <div className="relative flex items-center justify-start h-full">
            <span className="relative flex items-center justify-center">
                <input
                    type="checkbox"
                    ref={ref}
                    className={className + ' cursor-pointer'}
                    {...rest}
                />
                {!!rest.checked && !indeterminate && (
                    <div className="absolute flex items-center justify-center pointer-events-none">
                        <CheckIcon className="w-4 h-4 text-white" />
                    </div>
                )}
                {indeterminate && (
                    <div className="absolute flex items-center justify-center pointer-events-none">
                        <MinusIcon className="w-4 h-4 text-white" />
                    </div>
                )}
            </span>
        </div>
    );
}
type TFormValues = Pick<Submission, 'grade'>;

type GradeInputProps = React.DetailedHTMLProps<
    React.InputHTMLAttributes<HTMLInputElement>,
    HTMLInputElement
> &
    React.ComponentProps<typeof Input>;

const GradeInput = React.forwardRef<HTMLInputElement, GradeInputProps>(
    ({ className, ...props }, ref: React.ForwardedRef<HTMLInputElement>) => {
        return <Input ref={ref} autoComplete="off" {...props} className="capitalize" />;
    },
);
/**
 * Force Build
 *
 * @param param0
 *
 * @returns
 */
export function FileListAttachment({
    item,
    onDelete,
    showDownload,
    showDelete,
    showPreview,
}: {
    item: FileListItem;
    onDelete?: () => void;
    showDownload?: boolean;
    showDelete?: boolean;
    showPreview?: boolean;
}) {
    const { id } = useQueryParams(['id']);
    const fileRef = useRef<HTMLAnchorElement>();
    const selectIcon = useFileTypeImage();
    const { file } = item;
    const fileUrl =
        file && PDFTRON_VIEWABLE_FILE_TYPES.includes(file.fileType)
            ? `/courses/${id}/file-viewer?url=${encodeURIComponent(file?.url)}`
            : file.fileType == 'video'
            ? `/files/video?url=${file.url}`
            : file?.url;

    const [user] = useUserSession();
    const { toggle } = useUniversalModalDispatch();
    const invalidateQueries = useInvalidateQueries();
    const onEdit = () => {
        toggle(
            true,
            <SkipperPaperEditor
                fileEntryId={file.id}
                onSave={() => {
                    toggle(false);
                    invalidateQueries('CoursesActivitySubmissionsByEnrollments');
                }}
            />,
        );
    };

    return (
        <>
            <div
                className={`flex justify-between items-center p-2 rounded-md z-[3] ${getBackgroundColorForFile(
                    file?.originalFileContentType,
                )}`}>
                <a
                    className="flex items-center mr-3 h-full gap-x-2 truncate"
                    href={fileUrl}
                    rel="noreferrer"
                    target="_blank">
                    <div className="min-w-[39px] flex items-center justify-center">
                        {selectIcon({
                            type: file?.fileType || file?.originalFileContentType,
                            originalFileName: file?.originalFileName,
                            variant: true,
                        })}
                    </div>
                    <div className="truncate">
                        <p className="truncate text-sm font-[500] text-passport">{file.title}</p>
                        <p className="text-xs text-gray-500 font-[400]">{`${convertToBytes(
                            file.originalFileSize,
                        )}`}</p>
                    </div>
                </a>
                <div className="flex gap-x-2 items-center">
                    <Button
                        as="a"
                        size="small"
                        shape="fixedSquare"
                        variant="white"
                        href={fileUrl}
                        target="_blank"
                        rel="noreferrer">
                        <OpenSVG className="w-5 h-5 stroke-gray-500" />
                    </Button>
                    {showDownload && (
                        <Button
                            ref={fileRef}
                            as="a"
                            size="small"
                            shape="fixedSquare"
                            variant="white"
                            href={tryMakeDownloadUrl(file?.url)}
                            target="_blank"
                            rel="noreferrer"
                            download={file.title}>
                            <DownloadIcon className="w-5 h-5 stroke-gray-500" />
                        </Button>
                    )}

                    {showDelete && (
                        <>
                            {item.file.uploadedBy?.id === user.id &&
                                item.file.fileType === 'skipper_paper' && (
                                    <Button
                                        onClick={onEdit}
                                        size="small"
                                        shape="fixedSquare"
                                        variant="white">
                                        <PencilIcon className="w-5 h-5 stroke-gray-500" />
                                    </Button>
                                )}
                            <Button
                                onClick={() => {
                                    toggle(
                                        true,
                                        <SimpleModal
                                            type="warning"
                                            title={`Delete "${item.file.title}"?`}
                                            body={
                                                'Are you sure you want to delete this file? It cannot be recovered once it’s deleted'
                                            }
                                            confirmLabel={'Delete file'}
                                            onConfirm={onDelete}
                                        />,
                                    );
                                }}
                                size="small"
                                shape="fixedSquare"
                                variant="white">
                                <TrashIcon className="w-5 h-5 stroke-gray-500" />
                            </Button>
                        </>
                    )}
                </div>
            </div>
            {showPreview && (
                <>
                    {(item.file.fileType == 'image' || item.file.fileType == 'gif') && (
                        <div>
                            <img
                                src={item.file.url}
                                className="max-h-64 max-w-[75%]"
                                onError={(e) =>
                                    (e.target as HTMLImageElement).classList.add('hidden')
                                }
                            />
                        </div>
                    )}
                </>
            )}
        </>
    );
}
